import axios, { AxiosError, type AxiosInstance } from 'axios';
import { ensureAbsoluteHttpUrl } from './ensureAbsoluteHttpUrl';
import { resolveLanUrl } from './resolveLanUrl';
import { getRuntimeEnv } from './runtimeConfig';
import { logger } from './logger';

/**
 * Single axios instance for all backend / 3rd-party calls.
 *
 * Reads the base URL from `REACT_APP_API_BASE_URL`, at runtime in containers and
 * from `.env` locally (see `runtimeConfig`). An empty value here would make the
 * browser resolve every path against the frontend origin, where the nginx SPA
 * fallback answers with index.html instead of JSON. A hostname without
 * `https://` is also treated as a relative path; `ensureAbsoluteHttpUrl` prefixes
 * the scheme so that cannot happen.
 */

const baseURL = resolveLanUrl(
  ensureAbsoluteHttpUrl(getRuntimeEnv('REACT_APP_API_BASE_URL') ?? ''),
);

export const apiClient: AxiosInstance = axios.create({
  baseURL,
  withCredentials: true,
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

apiClient.interceptors.response.use(
  (res) => res,
  (error: AxiosError) => {
    // Single point to plug in: toast, Sentry, analytics, token refresh, etc.
    logger.error('[HAAT Partner Connect] api error', {
      url: error.config?.url,
      status: error.response?.status,
      message: error.message,
    });
    return Promise.reject(error);
  },
);
